import React from 'react';
import { Printer } from 'lucide-react';
import Header from '../components/common/Header.jsx';
import Footer from '../components/common/Footer.jsx';
import { useTheme } from '../context/ThemeContext.jsx';

const TermsConditions = () => {
  const { 
    colors, 
    spacing, 
    fonts,
    borderRadius,
    shadows
  } = useTheme();

  const pageStyles = {
    minHeight: '100vh',
    backgroundColor: colors.background,
    color: colors.text,
    fontFamily: fonts.families.primary,
    lineHeight: fonts.lineHeights.relaxed,
    transition: 'all 0.3s ease'
  };

  const mainStyles = {
    maxWidth: '900px',
    margin: `${spacing.scale['2xl']} auto`,
    padding: `0 ${spacing.scale.lg}`
  };

  const contentContainerStyles = {
    backgroundColor: colors.surface,
    borderRadius: borderRadius.xl,
    boxShadow: shadows.xl,
    overflow: 'hidden',
    border: `1px solid ${colors.border}`
  };

  const headerSectionStyles = {
    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryHover} 100%)`,
    color: colors.textInverse,
    padding: `${spacing.scale.lg} ${spacing.scale.md}`, // Even smaller padding
    textAlign: 'center'
  };

  const titleStyles = {
    fontSize: fonts.sizes.xl.desktop, // Even smaller title
    marginBottom: spacing.scale.sm, // Minimal margin
    fontWeight: fonts.weights.bold,
    fontFamily: fonts.families.secondary,
    color: '#ffffff', // Force white text
    textShadow: `1px 1px 2px rgba(0, 0, 0, 0.5)` // Add shadow for visibility
  };

  const updatedStyles = {
    fontSize: fonts.sizes.sm.desktop,
    opacity: 0.95,
    backgroundColor: `${colors.info}33`,
    padding: `${spacing.scale.xs} ${spacing.scale.md}`, // Smaller padding
    borderRadius: borderRadius.full,
    display: 'inline-block',
    border: `1px solid ${colors.info}66`,
    color: '#ffffff', // Force white text
    marginBottom: spacing.scale.xs // Minimal margin
  };

  const contentStyles = {
    padding: `${spacing.scale.lg} ${spacing.scale.xl}` // Much smaller top padding
  };

  const tocStyles = {
    backgroundColor: colors.backgroundSecondary,
    borderRadius: borderRadius.lg,
    padding: spacing.scale.xl,
    marginBottom: spacing.scale['3xl'],
    borderLeft: `4px solid ${colors.secondary}`,
    border: `1px solid ${colors.border}`
  };

  const tocTitleStyles = {
    marginBottom: spacing.scale.lg,
    color: colors.text,
    fontSize: fonts.sizes.xl.desktop,
    fontWeight: fonts.weights.semibold,
    fontFamily: fonts.families.secondary
  };

  const tocLinkStyles = {
    color: colors.secondary,
    textDecoration: 'none',
    transition: 'color 0.3s ease'
  };

  const highlightBoxImportantStyles = {
    backgroundColor: `${colors.error}15`,
    border: `1px solid ${colors.error}`,
    borderRadius: borderRadius.md,
    padding: spacing.scale.xl,
    margin: `${spacing.scale.xl} 0`
  };

  const highlightBoxInfoStyles = {
    backgroundColor: `${colors.info}15`,
    border: `1px solid ${colors.info}`,
    borderRadius: borderRadius.md,
    padding: spacing.scale.xl,
    margin: `${spacing.scale.xl} 0`
  };

  const highlightBoxWarningStyles = {
    backgroundColor: `${colors.warning}15`,
    border: `2px solid ${colors.warning}`,
    borderRadius: borderRadius.md,
    padding: spacing.scale.xl,
    margin: `${spacing.scale.xl} 0`
  };

  const sectionStyles = {
    marginBottom: spacing.scale['3xl']
  };

  const sectionHeadingStyles = {
    color: colors.text,
    fontSize: fonts.sizes['2xl'].desktop,
    marginBottom: spacing.scale.xl,
    paddingBottom: spacing.scale.sm,
    borderBottom: `2px solid ${colors.border}`,
    fontFamily: fonts.families.secondary,
    fontWeight: fonts.weights.semibold
  };

  const subHeadingStyles = {
    color: colors.textSecondary,
    fontSize: fonts.sizes.xl.desktop,
    margin: `${spacing.scale.xl} 0 ${spacing.scale.lg} 0`,
    fontWeight: fonts.weights.medium
  };

  const paragraphStyles = {
    marginBottom: spacing.scale.xl,
    fontSize: fonts.sizes.base.desktop,
    color: colors.text,
    lineHeight: fonts.lineHeights.relaxed
  };

  const listStyles = {
    marginBottom: spacing.scale.xl,
    paddingLeft: spacing.scale.xl,
    color: colors.text
  };

  const listItemStyles = {
    marginBottom: spacing.scale.md
  };

  const contactInfoStyles = {
    backgroundColor: colors.backgroundSecondary,
    borderRadius: borderRadius.lg,
    padding: spacing.scale.xl,
    marginTop: spacing.scale['3xl'],
    textAlign: 'center',
    border: `1px solid ${colors.border}`
  };

  const contactTitleStyles = {
    color: colors.text,
    marginBottom: spacing.scale.lg,
    fontSize: fonts.sizes.xl.desktop,
    fontWeight: fonts.weights.semibold,
    fontFamily: fonts.families.secondary
  };

  const contactLinkStyles = {
    color: colors.secondary,
    textDecoration: 'none',
    fontWeight: fonts.weights.medium,
    transition: 'color 0.3s ease'
  };

  const handleLinkHover = (e) => {
    e.target.style.color = colors.secondaryHover;
  };

  const handleLinkLeave = (e) => {
    e.target.style.color = colors.secondary;
  };

  const handleTocLinkHover = (e) => {
    e.target.style.color = colors.secondaryHover;
  };

  const handleTocLinkLeave = (e) => {
    e.target.style.color = colors.secondary;
  };

  return (
    <div style={pageStyles}>
      <Header showBreadcrumb={false} />
      
      <main style={mainStyles}>
        <div style={contentContainerStyles}>
          <section style={headerSectionStyles}>
            <h1 style={titleStyles}>Terms and Conditions</h1>
            <div style={updatedStyles}>Last Updated: July 16, 2026</div>
            {/* <div style={{ marginTop: spacing.scale.xs }}>
              <DocumentDownload 
                document={{
                  name: "Terms and Conditions",
                  filename: "terms-conditions",
                  type: "terms"
                }}
              />
            </div> */}
            <div style={{ marginTop: spacing.scale.xs, display: 'flex', justifyContent: 'center' }}>
              <button
                onClick={() => window.print()}
                style={{
                  backgroundColor: colors.secondary,
                  color: colors.textInverse,
                  border: 'none',
                  padding: `${spacing.scale.sm} ${spacing.scale.lg}`,
                  borderRadius: borderRadius.md,
                  fontSize: fonts.sizes.sm.desktop,
                  fontWeight: fonts.weights.medium,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.scale.xs
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = colors.secondaryHover;
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = colors.secondary;
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <Printer size={16} aria-hidden="true" /> Print Document
              </button>
            </div>
          </section>

          <div style={contentStyles}>
            <section style={tocStyles}>
              <h2 style={tocTitleStyles}>Table of Contents</h2>
              <ol style={{ paddingLeft: spacing.scale.xl }}>
                <li style={{ marginBottom: spacing.scale.sm }}>
                  <a href="#the-basics" style={tocLinkStyles} onMouseEnter={handleTocLinkHover} onMouseLeave={handleTocLinkLeave}>The Basics</a>
                </li>
                <li style={{ marginBottom: spacing.scale.sm }}>
                  <a href="#what-we-offer" style={tocLinkStyles} onMouseEnter={handleTocLinkHover} onMouseLeave={handleTocLinkLeave}>What We Offer</a>
                </li>
                <li style={{ marginBottom: spacing.scale.sm }}>
                  <a href="#absolutely-prohibited" style={tocLinkStyles} onMouseEnter={handleTocLinkHover} onMouseLeave={handleTocLinkLeave}>What's Absolutely Prohibited</a>
                </li>
                <li style={{ marginBottom: spacing.scale.sm }}>
                  <a href="#age-requirements" style={tocLinkStyles} onMouseEnter={handleTocLinkHover} onMouseLeave={handleTocLinkLeave}>Age Requirements</a>
                </li>
                <li style={{ marginBottom: spacing.scale.sm }}>
                  <a href="#payment-terms" style={tocLinkStyles} onMouseEnter={handleTocLinkHover} onMouseLeave={handleTocLinkLeave}>Payment Terms</a>
                </li>
                <li style={{ marginBottom: spacing.scale.sm }}>
                  <a href="#software-licensing" style={tocLinkStyles} onMouseEnter={handleTocLinkHover} onMouseLeave={handleTocLinkLeave}>Software Licensing</a>
                </li>
                <li style={{ marginBottom: spacing.scale.sm }}>
                  <a href="#digital-content" style={tocLinkStyles} onMouseEnter={handleTocLinkHover} onMouseLeave={handleTocLinkLeave}>Digital Content Rules</a>
                </li>
                <li style={{ marginBottom: spacing.scale.sm }}>
                  <a href="#merchandise" style={tocLinkStyles} onMouseEnter={handleTocLinkHover} onMouseLeave={handleTocLinkLeave}>Merchandise & Returns</a>
                </li>
                <li style={{ marginBottom: spacing.scale.sm }}>
                  <a href="#intellectual-property" style={tocLinkStyles} onMouseEnter={handleTocLinkHover} onMouseLeave={handleTocLinkLeave}>Intellectual Property</a>
                </li>
                <li style={{ marginBottom: spacing.scale.sm }}>
                  <a href="#privacy-data" style={tocLinkStyles} onMouseEnter={handleTocLinkHover} onMouseLeave={handleTocLinkLeave}>Privacy & Your Data</a>
                </li>
                <li style={{ marginBottom: spacing.scale.sm }}>
                  <a href="#liability-limits" style={tocLinkStyles} onMouseEnter={handleTocLinkHover} onMouseLeave={handleTocLinkLeave}>What We're Responsible For</a>
                </li>
                <li style={{ marginBottom: spacing.scale.sm }}>
                  <a href="#termination" style={tocLinkStyles} onMouseEnter={handleTocLinkHover} onMouseLeave={handleTocLinkLeave}>When Things End</a>
                </li>
                <li style={{ marginBottom: spacing.scale.sm }}>
                  <a href="#legal-stuff" style={tocLinkStyles} onMouseEnter={handleTocLinkHover} onMouseLeave={handleTocLinkLeave}>Legal Stuff</a>
                </li>
                <li style={{ marginBottom: spacing.scale.sm }}>
                  <a href="#contact-us" style={tocLinkStyles} onMouseEnter={handleTocLinkHover} onMouseLeave={handleTocLinkLeave}>Questions?</a>
                </li>
              </ol>
            </section>

            <div style={highlightBoxImportantStyles}>
              <h3 style={{ marginTop: 0, color: colors.error, fontWeight: fonts.weights.semibold }}>Zero Tolerance Policy</h3>
              <p style={{ ...paragraphStyles, marginBottom: 0 }}>
                <strong>Our products are NOT to be used for any criminal activity, illegal purposes, or in support of illegal activities.</strong> We take this seriously and will immediately terminate accounts and report suspected criminal use to appropriate authorities.
              </p>
            </div>

            <section style={sectionStyles} id="the-basics">
              <h2 style={sectionHeadingStyles}>1. The Basics</h2>
              
              <p style={paragraphStyles}>
                Welcome! These terms govern your use of Victoria Londe products and services. By using our software, buying our merchandise, or consuming our digital content, you agree to these terms.
              </p>
              
              <p style={paragraphStyles}>
                <strong>Simple version:</strong> At Victoria Londe, we create Software | Stories | Stuff for amazing people like you. These terms help protect our creative work and ensure a great experience for our entire community.
              </p>

              <div style={highlightBoxInfoStyles}>
                <h3 style={{ color: colors.info, marginTop: 0, fontWeight: fonts.weights.semibold }}>What This Covers</h3>
                <ul style={listStyles}>
                  <li style={listItemStyles}>All software applications and tools we create</li>
                  <li style={listItemStyles}>Digital content (games, stories, media)</li>
                  <li style={listItemStyles}>Physical merchandise and branded items</li>
                  <li style={listItemStyles}>Our website and any online services</li>
                  <li style={listItemStyles}>Support and documentation</li>
                </ul>
              </div>
            </section>

            <section style={sectionStyles} id="what-we-offer">
              <h2 style={sectionHeadingStyles}>2. What We Offer</h2>

              <h3 style={subHeadingStyles}>Software Development</h3>
              <ul style={listStyles}>
                <li style={listItemStyles}><strong>Software Applications:</strong> Our subscription-based software products and services</li>
                <li style={listItemStyles}><strong>Web Applications:</strong> Browser-based tools and platforms</li>
                <li style={listItemStyles}><strong>Mobile Apps:</strong> iOS and Android applications</li>
                <li style={listItemStyles}><strong>LondaLab Apps:</strong> Human Centered Intelligent Apps published through our LondaLab lab</li>
              </ul>

              <h3 style={subHeadingStyles}>Digital Content</h3>
              <ul style={listStyles}>
                <li style={listItemStyles}><strong>Early Reader and ESL Materials:</strong> Stories and companion apps for adult literacy and language learning</li>
                <li style={listItemStyles}><strong>Educational Resources:</strong> Printable worksheets, flashcards, and learning aids</li>
                <li style={listItemStyles}><strong>Video Content:</strong> Original video productions and series</li>
                <li style={listItemStyles}><strong>In-App Content:</strong> Creative elements and digital assets within our applications</li>
              </ul>

              <h3 style={subHeadingStyles}>Merchandise</h3>
              <ul style={listStyles}>
                <li style={listItemStyles}><strong>Branded Items:</strong> Clothing, accessories, and fun office supplies</li>
                <li style={listItemStyles}><strong>Workspace Essentials:</strong> Items to jazz up your desk and boost productivity</li>
                <li style={listItemStyles}><strong>Printed Materials:</strong> Physical books, posters, and collectibles</li>
              </ul>
            </section>

            <section style={sectionStyles} id="absolutely-prohibited">
              <h2 style={sectionHeadingStyles}>3. What's Absolutely Prohibited</h2>
              
              <div style={highlightBoxWarningStyles}>
                <h3 style={{ marginTop: 0, color: colors.warning, fontWeight: fonts.weights.semibold }}>Criminal Activity = Immediate Ban</h3>
                <p style={{ ...paragraphStyles, marginBottom: 0 }}>
                  We have <strong>ZERO TOLERANCE</strong> for criminal use of our products. This includes but is not limited to:
                </p>
              </div>

              <h3 style={subHeadingStyles}>Absolutely Forbidden Uses:</h3>
              <ul style={listStyles}>
                <li style={listItemStyles}><strong>Any illegal activity</strong> - fraud, theft, hacking, etc.</li>
                <li style={listItemStyles}><strong>Supporting criminal organizations</strong> - no matter how indirectly</li>
                <li style={listItemStyles}><strong>Money laundering</strong> or financial crimes</li>
                <li style={listItemStyles}><strong>Drug trafficking</strong> or illegal substance distribution</li>
                <li style={listItemStyles}><strong>Human trafficking</strong> or exploitation</li>
                <li style={listItemStyles}><strong>Terrorism</strong> or violent extremism</li>
                <li style={listItemStyles}><strong>Child exploitation</strong> of any kind</li>
                <li style={listItemStyles}><strong>Harassment, doxxing, or stalking</strong></li>
                <li style={listItemStyles}><strong>Creating malware</strong> or harmful software</li>
                <li style={listItemStyles}><strong>Copyright infringement</strong> or piracy</li>
                <li style={listItemStyles}><strong>Violating export laws</strong> or sanctions</li>
              </ul>

              <h3 style={subHeadingStyles}>What Happens If You Break These Rules:</h3>
              <ul style={listStyles}>
                <li style={listItemStyles}><strong>Immediate account termination</strong> - no warnings, no refunds</li>
                <li style={listItemStyles}><strong>All access revoked</strong> - software, content, everything</li>
                <li style={listItemStyles}><strong>Reported to authorities</strong> - we cooperate fully with law enforcement</li>
                <li style={listItemStyles}><strong>Legal action</strong> - we will pursue damages and injunctive relief</li>
                <li style={listItemStyles}><strong>Permanently banned</strong> - you can never use our products again</li>
              </ul>

              <p style={paragraphStyles}>
                <strong>We mean it.</strong> We monitor for suspicious activity and work with law enforcement. Don't test us on this.
              </p>
            </section>

            <section style={sectionStyles} id="age-requirements">
              <h2 style={sectionHeadingStyles}>4. Age Requirements</h2>
              
              <h3 style={subHeadingStyles}>Minimum Age: 13</h3>
              <p style={paragraphStyles}>
                You must be at least 13 years old to use our products. This is for legal compliance and because our content is rated PG-13 minimum.
              </p>

              <h3 style={subHeadingStyles}>Under 18?</h3>
              <p style={paragraphStyles}>If you're under 18, you need parent or guardian permission to:</p>
              <ul style={listStyles}>
                <li style={listItemStyles}>Make purchases</li>
                <li style={listItemStyles}>Create accounts</li>
                <li style={listItemStyles}>Use services that collect personal information</li>
                <li style={listItemStyles}>Download or install our software</li>
              </ul>

              <h3 style={subHeadingStyles}>Content Rating</h3>
              <p style={paragraphStyles}>Our digital content is rated <strong>PG-13 minimum</strong>. This means:</p>
              <ul style={listStyles}>
                <li style={listItemStyles}>May contain mild profanity</li>
                <li style={listItemStyles}>Some suggestive themes</li>
                <li style={listItemStyles}>Mild violence or intense situations</li>
                <li style={listItemStyles}>Complex topics requiring maturity</li>
              </ul>
            </section>

            <section style={sectionStyles} id="payment-terms">
              <h2 style={sectionHeadingStyles}>5. Payment Terms</h2>
              
              <h3 style={subHeadingStyles}>Pricing & Payment</h3>
              <ul style={listStyles}>
                <li style={listItemStyles}><strong>All prices in USD</strong> unless otherwise specified</li>
                <li style={listItemStyles}><strong>Payment due before delivery</strong> for most products</li>
                <li style={listItemStyles}><strong>Subscription services</strong> bill automatically</li>
                <li style={listItemStyles}><strong>Custom development</strong> may require deposits or milestone payments</li>
              </ul>

              <h3 style={subHeadingStyles}>Refunds</h3>
              <ul style={listStyles}>
                <li style={listItemStyles}><strong>Software:</strong> 30-day refund period if software doesn't work as described</li>
                <li style={listItemStyles}><strong>Digital content:</strong> Refunds within 14 days if content is fundamentally broken</li>
                <li style={listItemStyles}><strong>Merchandise:</strong> Returns accepted within 30 days in original condition</li>
                <li style={listItemStyles}><strong>Custom development:</strong> Refund policy specified in individual contracts</li>
              </ul>

              <h3 style={subHeadingStyles}>No Refunds For:</h3>
              <ul style={listStyles}>
                <li style={listItemStyles}>Account termination due to violations</li>
                <li style={listItemStyles}>Change of mind after using software/content</li>
                <li style={listItemStyles}>Compatibility issues we disclosed upfront</li>
                <li style={listItemStyles}>Custom work that meets agreed specifications</li>
              </ul>
            </section>

            <section style={sectionStyles} id="software-licensing">
              <h2 style={sectionHeadingStyles}>6. Software Licensing</h2>
              
              <h3 style={subHeadingStyles}>What You Get</h3>
              <p style={paragraphStyles}>
                When you buy our software, you get a <strong>license to use it</strong> - you don't own the software itself. Think of it like buying a movie ticket: you can watch the movie, but you don't own the theater.
              </p>

              <h3 style={subHeadingStyles}>Your License Includes:</h3>
              <ul style={listStyles}>
                <li style={listItemStyles}><strong>Personal or business use</strong> as specified in your purchase</li>
                <li style={listItemStyles}><strong>Installation on permitted devices</strong></li>
                <li style={listItemStyles}><strong>Updates and bug fixes</strong> during the license period</li>
                <li style={listItemStyles}><strong>Basic technical support</strong></li>
              </ul>

              <h3 style={subHeadingStyles}>You Cannot:</h3>
              <ul style={listStyles}>
                <li style={listItemStyles}><strong>Reverse engineer</strong> or decompile the software</li>
                <li style={listItemStyles}><strong>Redistribute or resell</strong> our software</li>
                <li style={listItemStyles}><strong>Remove copyright notices</strong> or branding</li>
                <li style={listItemStyles}><strong>Use beyond license limits</strong> (users, devices, etc.)</li>
                <li style={listItemStyles}><strong>Create derivative works</strong> without permission</li>
              </ul>

              <h3 style={subHeadingStyles}>Open Source Components</h3>
              <p style={paragraphStyles}>
                Our software may include open source components. Those components remain under their original licenses, which we'll provide upon request.
              </p>
            </section>

            <section style={sectionStyles} id="digital-content">
              <h2 style={sectionHeadingStyles}>7. Digital Content Rules</h2>
              
              <h3 style={subHeadingStyles}>Business Software</h3>
              <p style={paragraphStyles}>Our business software applications are licensed for commercial use:</p>
              <ul style={listStyles}>
                <li style={listItemStyles}><strong>Use in your business</strong> - exactly what it's designed for!</li>
                <li style={listItemStyles}><strong>Process your business data</strong> - that's the whole point</li>
                <li style={listItemStyles}><strong>Generate business insights</strong> - go make money with our tools</li>
                <li style={listItemStyles}><strong>Scale with your team</strong> - within your license limits</li>
              </ul>

              <h3 style={subHeadingStyles}>Entertainment & Creative Content</h3>
              <p style={paragraphStyles}>Our stories, games, and creative content are licensed for personal enjoyment:</p>
              <ul style={listStyles}>
                <li style={listItemStyles}><strong>Enjoy it personally</strong> - read, play, experience</li>
                <li style={listItemStyles}><strong>Share your thoughts</strong> - reviews, discussions, fair use commentary</li>
                <li style={listItemStyles}><strong>Create fan content</strong> - within reasonable fair use limits</li>
              </ul>

              <h3 style={subHeadingStyles}>What You Cannot Do (All Content Types):</h3>
              <ul style={listStyles}>
                <li style={listItemStyles}><strong>Redistribute our software/content</strong> - no sharing files or accounts</li>
                <li style={listItemStyles}><strong>Circumvent access controls</strong> - no hacking or cracking</li>
                <li style={listItemStyles}><strong>Resell our products</strong> - you can't become a reseller without permission</li>
                <li style={listItemStyles}><strong>Remove licensing/copyright notices</strong> - keep our credits intact</li>
                <li style={listItemStyles}><strong>Reverse engineer</strong> - except where legally permitted</li>
              </ul>

              <h3 style={subHeadingStyles}>User-Generated Content</h3>
              <p style={paragraphStyles}>If our software or platforms allow you to create content:</p>
              <ul style={listStyles}>
                <li style={listItemStyles}><strong>You own your original creations</strong></li>
                <li style={listItemStyles}><strong>You grant us license to host/display</strong> your content</li>
                <li style={listItemStyles}><strong>Follow the same prohibition rules</strong> - no illegal or harmful content</li>
                <li style={listItemStyles}><strong>We can remove content</strong> that violates these terms</li>
              </ul>
            </section>

            <section style={sectionStyles} id="merchandise">
              <h2 style={sectionHeadingStyles}>8. Merchandise & Returns</h2>
              
              <h3 style={subHeadingStyles}>Physical Products</h3>
              <p style={paragraphStyles}>
                We offer tech-themed merchandise and branded items for people who want to jazz up their workspace.
              </p>

              <h3 style={subHeadingStyles}>Shipping</h3>
              <ul style={listStyles}>
                <li style={listItemStyles}><strong>Processing time:</strong> Varies by product - we strive for 1-3 business days when possible</li>
                <li style={listItemStyles}><strong>Shipping estimates:</strong> Communicated before final purchase/payment</li>
                <li style={listItemStyles}><strong>International:</strong> Additional customs fees may apply</li>
                <li style={listItemStyles}><strong>Tracking:</strong> Provided for most shipments</li>
              </ul>

              <h3 style={subHeadingStyles}>Returns & Exchanges</h3>
              <ul style={listStyles}>
                <li style={listItemStyles}><strong>30 days:</strong> From delivery date</li>
                <li style={listItemStyles}><strong>Original condition:</strong> Unworn, unused, with tags</li>
                <li style={listItemStyles}><strong>Return shipping:</strong> Customer pays unless item is defective</li>
                <li style={listItemStyles}><strong>Refund time:</strong> 5-10 business days after we receive the return</li>
              </ul>

              <h3 style={subHeadingStyles}>Defective Items</h3>
              <p style={paragraphStyles}>If something arrives broken or defective, we'll make it right:</p>
              <ul style={listStyles}>
                <li style={listItemStyles}><strong>Contact us immediately</strong> with photos</li>
                <li style={listItemStyles}><strong>We pay return shipping</strong> for defective items</li>
                <li style={listItemStyles}><strong>Full refund or replacement</strong> - your choice</li>
              </ul>
            </section>

            <section style={sectionStyles} id="intellectual-property">
              <h2 style={sectionHeadingStyles}>9. Intellectual Property</h2>
              
              <h3 style={subHeadingStyles}>Our Stuff</h3>
              <p style={paragraphStyles}>We own or license all the intellectual property in our products:</p>
              <ul style={listStyles}>
                <li style={listItemStyles}><strong>Software code and architecture</strong></li>
                <li style={listItemStyles}><strong>Story content and characters</strong></li>
                <li style={listItemStyles}><strong>Art, graphics, and designs</strong></li>
                <li style={listItemStyles}><strong>Trademarks and branding</strong></li>
                <li style={listItemStyles}><strong>Documentation and tutorials</strong></li>
              </ul>

              <h3 style={subHeadingStyles}>Respecting Others</h3>
              <p style={paragraphStyles}>We respect intellectual property rights and expect you to do the same. If you believe our content infringes your rights, contact us with:</p>
              <ul style={listStyles}>
                <li style={listItemStyles}>Description of the copyrighted work</li>
                <li style={listItemStyles}>Location of the allegedly infringing content</li>
                <li style={listItemStyles}>Your contact information</li>
                <li style={listItemStyles}>Statement of good faith belief</li>
                <li style={listItemStyles}>Statement under penalty of perjury</li>
                <li style={listItemStyles}>Electronic or physical signature</li>
              </ul>

              <h3 style={subHeadingStyles}>DMCA Compliance</h3>
              <p style={paragraphStyles}>
                We comply with the Digital Millennium Copyright Act. Send DMCA notices to: <strong>hello@victorialonde.com</strong>
              </p>
            </section>

            <section style={sectionStyles} id="privacy-data">
              <h2 style={sectionHeadingStyles}>10. Privacy & Your Data</h2>
              
              <h3 style={subHeadingStyles}>What We Collect</h3>
              <p style={paragraphStyles}>We collect only what we need to provide our services:</p>
              <ul style={listStyles}>
                <li style={listItemStyles}><strong>Account information</strong> - name, email, payment details</li>
                <li style={listItemStyles}><strong>Usage data</strong> - how you use our software (to improve it)</li>
                <li style={listItemStyles}><strong>Support communications</strong> - when you contact us for help</li>
                <li style={listItemStyles}><strong>Basic analytics</strong> - website traffic and performance</li>
              </ul>

              <h3 style={subHeadingStyles}>What We Don't Do</h3>
              <ul style={listStyles}>
                <li style={listItemStyles}><strong>Sell your data</strong> - never, ever</li>
                <li style={listItemStyles}><strong>Spam you</strong> - only necessary communications</li>
                <li style={listItemStyles}><strong>Track excessively</strong> - minimal analytics only</li>
                <li style={listItemStyles}><strong>Share without permission</strong> - except when required by law</li>
              </ul>

              <p style={paragraphStyles}>
                For complete details, see our <a 
                  href="/privacy-policy" 
                  style={{ color: colors.secondary, textDecoration: 'none' }}
                  onMouseEnter={handleLinkHover}
                  onMouseLeave={handleLinkLeave}
                >Privacy Policy</a>.
              </p>
            </section>

            <section style={sectionStyles} id="liability-limits">
              <h2 style={sectionHeadingStyles}>11. What We're Responsible For</h2>
              
              <h3 style={subHeadingStyles}>Our Promise</h3>
              <p style={paragraphStyles}>We promise to:</p>
              <ul style={listStyles}>
                <li style={listItemStyles}><strong>Deliver working software</strong> that meets our specifications</li>
                <li style={listItemStyles}><strong>Provide described content</strong> and merchandise</li>
                <li style={listItemStyles}><strong>Fix bugs</strong> and provide reasonable support</li>
                <li style={listItemStyles}><strong>Protect your data</strong> with reasonable security measures</li>
                <li style={listItemStyles}><strong>Honor refund policies</strong> as stated</li>
              </ul>

              <h3 style={subHeadingStyles}>Reality Check</h3>
              <p style={paragraphStyles}>Software is complex, and we're human. We can't guarantee:</p>
              <ul style={listStyles}>
                <li style={listItemStyles}><strong>100% uptime</strong> - servers sometimes go down</li>
                <li style={listItemStyles}><strong>Bug-free code</strong> - we fix issues as we find them</li>
                <li style={listItemStyles}><strong>Compatibility</strong> with every possible system configuration</li>
                <li style={listItemStyles}><strong>Unlimited support</strong> - we provide reasonable assistance</li>
                <li style={listItemStyles}><strong>Future availability</strong> - we may discontinue products</li>
              </ul>

              <h3 style={subHeadingStyles}>Limitation of Liability</h3>
              <p style={paragraphStyles}>
                If something goes wrong that's our fault, our liability is limited to the amount you paid for the specific product. We're not liable for:
              </p>
              <ul style={listStyles}>
                <li style={listItemStyles}>Lost profits or business opportunities</li>
                <li style={listItemStyles}>Data loss (keep backups!)</li>
                <li style={listItemStyles}>Indirect or consequential damages</li>
                <li style={listItemStyles}>Issues caused by misuse of our products</li>
              </ul>

              <p style={paragraphStyles}>
                <strong>Translation:</strong> If our $10 app breaks, you can get your $10 back, but we're not buying you a new computer.
              </p>
            </section>

            <section style={sectionStyles} id="termination">
              <h2 style={sectionHeadingStyles}>12. When Things End</h2>
              
              <h3 style={subHeadingStyles}>You Can Leave Anytime</h3>
              <p style={paragraphStyles}>
                You can stop using our products whenever you want. For subscriptions, cancel before your next billing cycle to avoid charges.
              </p>

              <h3 style={subHeadingStyles}>When We End Things</h3>
              <p style={paragraphStyles}>We can terminate accounts for:</p>
              <ul style={listStyles}>
                <li style={listItemStyles}><strong>Violation of these terms</strong> - especially criminal activity</li>
                <li style={listItemStyles}><strong>Non-payment</strong> - if you don't pay what you owe</li>
                <li style={listItemStyles}><strong>Abuse of support</strong> - being unreasonably demanding or abusive</li>
                <li style={listItemStyles}><strong>Fraudulent activity</strong> - chargebacks, fake information, etc.</li>
              </ul>

              <h3 style={subHeadingStyles}>What Happens After Termination</h3>
              <ul style={listStyles}>
                <li style={listItemStyles}><strong>Software stops working</strong> - no more updates or support</li>
                <li style={listItemStyles}><strong>Subscription services end</strong> - ongoing services are discontinued</li>
                <li style={listItemStyles}><strong>Account access may be limited</strong> - access to purchased content varies by product type</li>
                <li style={listItemStyles}><strong>Data may be deleted</strong> - download what you need first</li>
                <li style={listItemStyles}><strong>No refunds</strong> for violations</li>
              </ul>

              <h3 style={subHeadingStyles}>Discontinued Products</h3>
              <p style={paragraphStyles}>Sometimes we have to discontinue products. When that happens:</p>
              <ul style={listStyles}>
                <li style={listItemStyles}><strong>90 days notice</strong> when possible</li>
                <li style={listItemStyles}><strong>Final updates</strong> to ensure stability</li>
                <li style={listItemStyles}><strong>Data export tools</strong> when applicable</li>
                <li style={listItemStyles}><strong>No ongoing support</strong> after discontinuation</li>
              </ul>
            </section>

            <section style={sectionStyles} id="legal-stuff">
              <h2 style={sectionHeadingStyles}>13. Legal Stuff</h2>
              
              <h3 style={subHeadingStyles}>Governing Law</h3>
              <p style={paragraphStyles}>
                These terms are governed by California law. Any disputes will be resolved in California courts.
              </p>

              <h3 style={subHeadingStyles}>Dispute Resolution</h3>
              <p style={paragraphStyles}>If we have a disagreement:</p>
              <ol style={listStyles}>
                <li style={listItemStyles}><strong>Talk to us first</strong> - contact support to resolve issues</li>
                <li style={listItemStyles}><strong>Try mediation</strong> - we'll attempt good faith negotiation</li>
                <li style={listItemStyles}><strong>Small claims court</strong> - for smaller disputes</li>
                <li style={listItemStyles}><strong>Arbitration</strong> - for larger disputes (individual basis only, no class actions)</li>
              </ol>

              <h3 style={subHeadingStyles}>Changes to These Terms</h3>
              <p style={paragraphStyles}>We may update these terms occasionally. When we do:</p>
              <ul style={listStyles}>
                <li style={listItemStyles}><strong>30 days notice</strong> for material changes</li>
                <li style={listItemStyles}><strong>Email notification</strong> to active users</li>
                <li style={listItemStyles}><strong>Website posting</strong> with effective date</li>
                <li style={listItemStyles}><strong>Continued use</strong> means you accept the changes</li>
              </ul>

              <h3 style={subHeadingStyles}>Severability</h3>
              <p style={paragraphStyles}>
                If any part of these terms is found invalid, the rest still applies.
              </p>

              <h3 style={subHeadingStyles}>No Waiver</h3>
              <p style={paragraphStyles}>
                Just because we don't immediately enforce something doesn't mean we've waived our rights.
              </p>
            </section>

            <section style={sectionStyles} id="contact-us">
              <h2 style={sectionHeadingStyles}>14. Questions?</h2>
              
              <div style={contactInfoStyles}>
                <h3 style={contactTitleStyles}>Get In Touch</h3>
                <p style={paragraphStyles}>Questions about these terms? Need help with our products? We're here to help:</p>
                <p style={paragraphStyles}>
                  <strong>General Questions:</strong> <a 
                    href="mailto:hello@victorialonde.com" 
                    style={contactLinkStyles}
                    onMouseEnter={handleLinkHover}
                    onMouseLeave={handleLinkLeave}
                  >hello@victorialonde.com</a><br />
                  <strong>Legal/Terms Questions:</strong> <a 
                    href="mailto:hello@victorialonde.com" 
                    style={contactLinkStyles}
                    onMouseEnter={handleLinkHover}
                    onMouseLeave={handleLinkLeave}
                  >hello@victorialonde.com</a><br />
                  <strong>DMCA Notices:</strong> <a 
                    href="mailto:hello@victorialonde.com" 
                    style={contactLinkStyles}
                    onMouseEnter={handleLinkHover}
                    onMouseLeave={handleLinkLeave}
                  >hello@victorialonde.com</a><br />
                  <strong>Report Criminal Activity:</strong> <a 
                    href="mailto:hello@victorialonde.com" 
                    style={contactLinkStyles}
                    onMouseEnter={handleLinkHover}
                    onMouseLeave={handleLinkLeave}
                  >hello@victorialonde.com</a>
                </p>
                <p style={{ ...paragraphStyles, marginBottom: 0 }}>
                  <strong>Victoria Londe</strong><br />
                  2883 Hopyard Rd<br />
                  Pleasanton, CA 94588
                </p>
              </div>
            </section>

            <div style={highlightBoxInfoStyles}>
              <h3 style={{ color: colors.info, marginTop: 0, fontWeight: fonts.weights.semibold }}>The Bottom Line</h3>
              <p style={{ ...paragraphStyles, marginBottom: 0 }}>
                We want to create great software and content while maintaining a safe, legal community. Use our products responsibly, don't do illegal stuff, and we'll get along great. If you have questions, just ask - we're friendly people who happen to take legal compliance very seriously.
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TermsConditions;